import { create } from 'zustand';
import type { Conversation } from './chatStore';
import waApi from '../../services/whatsapp/waApi';

/**
 * Ticket do Kanban WhatsApp (separado do Kanban geral do sistema).
 * Usa a mesma interface de KanbanCard.tsx em src/pages/whatsapp/.
 */
export interface KanbanTicket {
  id: string;
  title: string;
  client: string;
  category: string;
  priority: string;
  assignee: string;
  assigneeAvatar: string;
  dueDate: string | null;
  columnId: string;
  estimatedHours?: number;
  description?: string;
  /** ID da conversa WhatsApp que originou este ticket */
  conversationId?: string;
  /** Resumo gerado pela IA ao finalizar */
  aiSummary?: string;
  /** Data de criacao do card (ISO 8601), vinda do backend; ausente em cards locais */
  createdAt?: string;
}

interface KanbanState {
  tickets: KanbanTicket[];
  loaded: boolean;
  fetchTickets: () => Promise<void>;
  addTicket: (ticket: KanbanTicket) => void;
  moveTicket: (ticketId: string, newColumnId: string) => void;
  updateTicket: (ticketId: string, updates: Partial<KanbanTicket>) => void;
  deleteTicket: (ticketId: string) => void;
  /** Cria ticket automaticamente a partir de uma conversa WhatsApp */
  createFromConversation: (conversation: Conversation, agentName: string) => string;
  /** Move ticket para "done" e salva resumo IA */
  completeTicket: (ticketId: string, aiSummary?: string) => void;
  /** Busca ticket por conversationId */
  getByConversation: (conversationId: string) => KanbanTicket | undefined;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

function detectCategory(conversation: Conversation): string {
  const text = `${conversation.subject || ''} ${conversation.last_message || ''}`.toLowerCase();
  if (
    text.includes('irpf') ||
    text.includes('imposto') ||
    text.includes('fiscal') ||
    text.includes('nota')
  )
    return 'fiscal';
  if (
    text.includes('folha') ||
    text.includes('ferias') ||
    text.includes('rescisao') ||
    text.includes('pessoal')
  )
    return 'pessoal';
  if (text.includes('balancete') || text.includes('contabil') || text.includes('dre'))
    return 'contabil';
  if (
    text.includes('abertura') ||
    text.includes('societario') ||
    text.includes('holding') ||
    text.includes('contrato')
  )
    return 'societario';
  return 'consultoria';
}

/** Mapeia documento da API para KanbanTicket do frontend. */
function apiToTicket(doc: any): KanbanTicket {
  return {
    id: doc.id, // eslint-disable-line no-underscore-dangle
    title: doc.title || '',
    client: doc.client || '',
    category: doc.category || 'consultoria',
    priority: doc.priority || 'medium',
    assignee: doc.assignee || '',
    assigneeAvatar: doc.assignee_avatar || doc.assigneeAvatar || '',
    dueDate: doc.due_date || doc.dueDate || null,
    columnId: doc.column_id || doc.columnId || 'todo',
    estimatedHours: doc.estimated_hours || doc.estimatedHours,
    description: doc.description,
    conversationId: doc.conversation_id || doc.conversationId,
    aiSummary: doc.ai_summary || doc.aiSummary,
    createdAt: doc.created_at || doc.createdAt || undefined,
  };
}

export const useKanbanStore = create<KanbanState>()((set, get) => ({
  tickets: [],
  loaded: false,

  fetchTickets: async () => {
    try {
      const res = await waApi.get('/kanban-tasks');
      const items = res.data?.items ?? res.data ?? [];
      set({ tickets: items.map(apiToTicket), loaded: true });
    } catch {
      set({ loaded: true });
    }
  },

  addTicket: ticket => {
    set(state => ({ tickets: [...state.tickets, ticket] }));
    waApi
      .post('/kanban-tasks', {
        title: ticket.title,
        client: ticket.client,
        category: ticket.category,
        priority: ticket.priority,
        assignee: ticket.assignee,
        assignee_avatar: ticket.assigneeAvatar,
        due_date: ticket.dueDate,
        column_id: ticket.columnId,
        estimated_hours: ticket.estimatedHours,
        description: ticket.description,
        conversation_id: ticket.conversationId,
      })
      .then(res => {
        const created = apiToTicket(res.data);
        // Substitui o ticket local pelo retornado (com ID real)
        set(state => ({
          tickets: state.tickets.map(t => (t.id === ticket.id ? created : t)),
        }));
      })
      .catch(() => {});
  },

  moveTicket: (ticketId, newColumnId) => {
    set(state => ({
      tickets: state.tickets.map(t => (t.id === ticketId ? { ...t, columnId: newColumnId } : t)),
    }));
    waApi.put(`/kanban-tasks/${ticketId}`, { column_id: newColumnId }).catch(() => {});
  },

  updateTicket: (ticketId, updates) => {
    set(state => ({
      tickets: state.tickets.map(t => (t.id === ticketId ? { ...t, ...updates } : t)),
    }));
    const payload: Record<string, any> = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.client !== undefined) payload.client = updates.client;
    if (updates.category !== undefined) payload.category = updates.category;
    if (updates.priority !== undefined) payload.priority = updates.priority;
    if (updates.assignee !== undefined) payload.assignee = updates.assignee;
    if (updates.assigneeAvatar !== undefined) payload.assignee_avatar = updates.assigneeAvatar;
    if (updates.dueDate !== undefined) payload.due_date = updates.dueDate;
    if (updates.columnId !== undefined) payload.column_id = updates.columnId;
    if (updates.estimatedHours !== undefined) payload.estimated_hours = updates.estimatedHours;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.aiSummary !== undefined) payload.ai_summary = updates.aiSummary;
    if (Object.keys(payload).length > 0) {
      waApi.put(`/kanban-tasks/${ticketId}`, payload).catch(() => {});
    }
  },

  deleteTicket: ticketId => {
    set(state => ({
      tickets: state.tickets.filter(t => t.id !== ticketId),
    }));
    waApi.delete(`/kanban-tasks/${ticketId}`).catch(() => {});
  },

  createFromConversation: (conversation, agentName) => {
    // Evita duplicar ticket para a mesma conversa
    const existing = get().tickets.find(t => t.conversationId === conversation.id);
    if (existing) return existing.id;

    const ticketId = `wa-${Date.now()}`;
    const category = detectCategory(conversation);

    const ticket: KanbanTicket = {
      id: ticketId,
      title: conversation.subject || `Atendimento - ${conversation.client_name}`,
      client: conversation.client_name,
      category,
      priority: conversation.priority || 'medium',
      assignee: agentName,
      assigneeAvatar: getInitials(agentName),
      dueDate: null,
      columnId: 'todo',
      description: conversation.last_message || '',
      conversationId: conversation.id,
    };

    // addTicket ja faz o POST e substitui o ID
    get().addTicket(ticket);
    return ticketId;
  },

  completeTicket: (ticketId, aiSummary) => {
    set(state => ({
      tickets: state.tickets.map(t =>
        t.id === ticketId ? { ...t, columnId: 'done', aiSummary: aiSummary || t.aiSummary } : t,
      ),
    }));
    const payload: Record<string, any> = { column_id: 'done' };
    if (aiSummary) payload.ai_summary = aiSummary;
    waApi.put(`/kanban-tasks/${ticketId}`, payload).catch(() => {});
  },

  getByConversation: conversationId => get().tickets.find(t => t.conversationId === conversationId),
}));
